/**
 * The owner assistant's API surface (/api/assistant).
 *
 * `apiRequest` returns the raw envelope — it does not unwrap — so every call
 * here destructures `.data` once and hands components a plain shape. That
 * keeps `json.data ?? json` out of the render tree.
 */
import { apiRequest } from './core';

export interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: { code?: string; message?: string };
}

export interface Unavailable {
  available: false;
  reason: string;
}

export interface RevenueWindow {
  total: number;
  orderCount: number;
  windowDays: number;
}

export interface BriefFacts {
  briefDate: string;
  revenue:
    | ({ available: true; currency: string; last7d: RevenueWindow; prior7d: RevenueWindow; last30d: RevenueWindow; prior30d: RevenueWindow })
    | Unavailable;
  customers: { total: number; new7d: number; new30d: number; subscribers: number };
  unfinished: Array<{ kind: string; id: string | null; label: string; ctaRoute: string; severity: number }>;
  holidays: Array<{ name: string; date: string; daysAway: number; campaignHint: string | null }>;
  caveats: string[];
}

export interface Brief {
  briefDate: string;
  status: string;
  narrative: string;
  narrativeSource: string;
  facts: BriefFacts;
  generatedAt: string | null;
  cached: boolean;
}

export interface PendingAction {
  actionId: string;
  toolName: string;
  confirmationToken: string;
  expiresAt: string;
  /** The binding a confirming surface hands back; releases nothing by itself. */
  confirmHash?: string;
}

/**
 * A parked send still inside its window, from GET /assistant/actions/pending.
 * Never carries the token: the session plus `confirmHash` is the web's proof.
 */
export interface PendingConfirmation {
  id: string;
  toolName: string;
  tier: string;
  source: string;
  hostName: string | null;
  conversationId: string | null;
  inputSummary: Record<string, string | number | boolean | null>;
  confirmHash: string;
  requestedAt: string;
  expiresAt: string;
}

export interface ChatTurn {
  conversationId: string;
  reply: string;
  pendingActions: PendingAction[];
  toolsUsed: string[];
}

export interface AssistantSettings {
  countryCode: string;
  timezone: string;
  mentorPreset: string;
  autonomyCeiling: string;
  /** How far the Chrome panel may go on a page in this workspace: B0 read, B1 navigate, B2 fill, B3 commit. */
  browserCeiling: string;
  completenessScore: number;
  credentials: {
    configured: boolean;
    keyLast4: string | null;
    /** Which model answers for this workspace. 'anthropic' when unconfigured. */
    provider: string;
    baseUrl: string | null;
    model: string | null;
  };
}

/**
 * What the tool-calling probe found. A model that answers fluently but never
 * calls a tool is the worst failure here — it describes the merchant's revenue
 * instead of reading it — so the endpoint proves itself before it is trusted.
 */
export type VerifyProviderResult =
  | { verified: true; reliability: string }
  | { verified: false; failedStep: string; detail: string };

/** Which model a merchant is bringing, beyond whose key it is. */
export interface ProviderChoice {
  readonly provider: 'anthropic' | 'openai-compatible' | 'xai';
  readonly baseUrl?: string;
  readonly model?: string;
}

/** The server's coded failures, so the UI can say something specific. */
export class AssistantApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'AssistantApiError';
  }
}

/** Shared with the agents surface (assistantAgentsApi.ts), which speaks the same envelope. */
export function unwrap<T>(envelope: Envelope<T>): T {
  if (envelope.success === false || envelope.error) {
    throw new AssistantApiError(
      envelope.error?.message ?? 'Request failed.',
      envelope.error?.code ?? 'UNKNOWN_ERROR',
    );
  }
  return (envelope.data ?? envelope) as T;
}

export interface WaitingInitiative {
  id: string;
  title: string;
  rationale: string;
  hasDraft: boolean;
}

export interface LedgerAction {
  id: string;
  toolName: string;
  tier: string;
  status: string;
  source: string;
  actorRole: string | null;
  createdAt: string;
  /** What the action made or changed, when the ledger knows (P2). */
  entityType?: string | null;
  entityId?: string | null;
  entityTitle?: string | null;
  entityUrl?: string | null;
  /** The server's verdict on whether Undo is offered — never inferred here. */
  revertable?: boolean;
  revertedAt?: string | null;
}

export interface AssistantTaskSummary {
  id: string;
  goal: string;
  status: string;
  stepIndex: number;
  toolCallsUsed: number;
  budgetToolCalls: number;
  lastError: string | null;
  createdAt: string;
  completedAt: string | null;
  planJson: unknown;
}

export interface AssistantTaskAction {
  id: string;
  toolName: string;
  tier: string;
  status: string;
  createdAt: string;
}

export interface AssistantTaskDetail extends AssistantTaskSummary {
  reportJson: unknown;
  actions: AssistantTaskAction[];
}

export interface RevertOutcome {
  entityType: string;
  entityId: string;
  kind: 'discard' | 'restore' | 'undiscard';
}

// A list answers an array even when the envelope lacks its key: the callers
// read `.length` and `.map` on what comes back, and a wire that disagrees
// with the type is a failed load, not a page-wide crash.
export const assistantApi = {
  /** Run the three-step tool-calling probe against the saved endpoint. */
  async verifyProvider(): Promise<VerifyProviderResult> {
    return unwrap(
      await apiRequest<Envelope<VerifyProviderResult>>('/assistant/credentials/verify', {
        method: 'POST',
      }),
    );
  },

  async listActions(limit = 20): Promise<LedgerAction[]> {
    const result = unwrap(
      await apiRequest<Envelope<{ actions?: LedgerAction[] }>>(`/assistant/actions?limit=${limit}`),
    );
    return result.actions ?? [];
  },

  async listInitiatives(): Promise<WaitingInitiative[]> {
    const result = unwrap(
      await apiRequest<Envelope<{ initiatives: WaitingInitiative[] }>>('/assistant/initiatives'),
    );
    return result.initiatives;
  },

  async decideInitiative(
    id: string,
    decision: 'ACCEPTED' | 'EDITED' | 'DISMISSED',
    note?: string,
  ): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/initiatives/${id}/decide`, {
        method: 'POST',
        body: JSON.stringify(note ? { decision, note } : { decision }),
      }),
    );
  },

  async getBrief(): Promise<Brief> {
    return unwrap(await apiRequest<Envelope<Brief>>('/assistant/brief'));
  },

  async regenerateBrief(): Promise<Brief> {
    return unwrap(
      await apiRequest<Envelope<Brief>>('/assistant/brief/regenerate', { method: 'POST' }),
    );
  },

  async chat(message: string, conversationId?: string): Promise<ChatTurn> {
    return unwrap(
      await apiRequest<Envelope<ChatTurn>>('/assistant/chat', {
        method: 'POST',
        body: JSON.stringify(conversationId ? { message, conversationId } : { message }),
      }),
    );
  },

  async confirmAction(actionId: string, confirmationToken: string): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/actions/${actionId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ confirmationToken }),
      }),
    );
  },

  /** The web's proof: the signed-in session, plus the hash the person was shown. */
  async confirmActionBySession(actionId: string, confirmHash: string): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/actions/${actionId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ confirmHash }),
      }),
    );
  },

  async listPendingActions(): Promise<PendingConfirmation[]> {
    const result = unwrap(
      await apiRequest<Envelope<{ pending?: PendingConfirmation[] }>>('/assistant/actions/pending'),
    );
    return result.pending ?? [];
  },

  async cancelAction(actionId: string): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/actions/${actionId}/cancel`, {
        method: 'POST',
      }),
    );
  },

  async createTask(goal: string, conversationId?: string): Promise<{ taskId: string }> {
    return unwrap(
      await apiRequest<Envelope<{ taskId: string }>>('/assistant/tasks', {
        method: 'POST',
        body: JSON.stringify({ goal, ...(conversationId ? { conversationId } : {}) }),
      }),
    );
  },

  async listTasks(limit = 20): Promise<AssistantTaskSummary[]> {
    const result = unwrap(
      await apiRequest<Envelope<{ tasks?: AssistantTaskSummary[] }>>(`/assistant/tasks?limit=${limit}`),
    );
    return result.tasks ?? [];
  },

  async getTask(taskId: string): Promise<AssistantTaskDetail> {
    return unwrap(await apiRequest<Envelope<AssistantTaskDetail>>(`/assistant/tasks/${taskId}`));
  },

  /** PLANNED → QUEUED. The only way a chat-made plan reaches the worker. */
  async approveTask(taskId: string): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/tasks/${taskId}/approve`, { method: 'POST' }),
    );
  },

  async cancelTask(taskId: string): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>(`/assistant/tasks/${taskId}/cancel`, { method: 'POST' }),
    );
  },

  /** Take a SUCCEEDED write back: discard what it made, or restore what it edited. Single-use. */
  async revertAction(actionId: string): Promise<RevertOutcome> {
    return unwrap(
      await apiRequest<Envelope<RevertOutcome & { reverted: true }>>(`/assistant/actions/${actionId}/revert`, {
        method: 'POST',
      }),
    );
  },

  async getSettings(): Promise<AssistantSettings> {
    return unwrap(await apiRequest<Envelope<AssistantSettings>>('/assistant/settings'));
  },

  async updateSettings(patch: Partial<AssistantSettings>): Promise<AssistantSettings> {
    return unwrap(
      await apiRequest<Envelope<AssistantSettings>>('/assistant/settings', {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    );
  },

  /** The key goes out and never comes back — only its last four characters do. */
  async saveKey(
    apiKey: string,
    choice: ProviderChoice = { provider: 'anthropic' },
  ): Promise<{ configured: boolean; keyLast4: string; provider: string }> {
    return unwrap(
      await apiRequest<Envelope<{ configured: boolean; keyLast4: string; provider: string }>>(
        '/assistant/credentials',
        {
          method: 'PUT',
          body: JSON.stringify({
            apiKey,
            provider: choice.provider,
            // A base URL is only sent for openai-compatible; the server does
            // not keep one it was not expecting. xAI's endpoint is fixed
            // server-side, so only the model travels for it.
            ...(choice.provider === 'openai-compatible'
              ? { baseUrl: choice.baseUrl, model: choice.model || undefined }
              : choice.provider === 'xai'
                ? { model: choice.model || undefined }
                : {}),
          }),
        },
      ),
    );
  },

  async removeKey(): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>('/assistant/credentials', { method: 'DELETE' }),
    );
  },

  // ── Knowledge the Responder answers from ─────────────────────────────

  async listKnowledge(): Promise<KnowledgeDocSummary[]> {
    const result = unwrap(await apiRequest<Envelope<{ docs?: KnowledgeDocSummary[] }>>('/assistant/knowledge'));
    return result.docs ?? [];
  },

  async addKnowledge(title: string, body: string, kind?: KnowledgeKind): Promise<void> {
    unwrap(
      await apiRequest<Envelope<unknown>>('/assistant/knowledge', {
        method: 'POST',
        body: JSON.stringify({ title, body, ...(kind ? { kind } : {}) }),
      }),
    );
  },

  /** A question/answer spreadsheet, read client-side and sent as text. */
  async importKnowledgeCsv(csv: string, kind?: KnowledgeKind): Promise<KnowledgeCsvImportResult> {
    return unwrap(
      await apiRequest<Envelope<KnowledgeCsvImportResult>>('/assistant/knowledge/csv', {
        method: 'POST',
        body: JSON.stringify({ csv, ...(kind ? { kind } : {}) }),
      }),
    );
  },

  async removeKnowledge(docId: string): Promise<void> {
    unwrap(await apiRequest<Envelope<unknown>>(`/assistant/knowledge/${docId}`, { method: 'DELETE' }));
  },
};

export type KnowledgeKind = 'FAQ' | 'POLICY' | 'HOURS' | 'SHIPPING' | 'PRICELIST' | 'CATALOG' | 'SITE' | 'OTHER';

export interface KnowledgeDocSummary {
  id: string;
  title: string;
  kind: string;
  sourceType: string;
  enabled: boolean;
  chunks: number;
}

export interface KnowledgeCsvImportResult {
  imported: number;
  duplicates: number;
  skipped: Array<{ row: number; reason: string }>;
}
